import { DollarSign, Truck, Palette, Package, PenSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { createFrontTemplate, createBackTemplate } from '../lib/templates';
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { CardTemplate } from './CardTemplate';
import { SubscriptionTiers } from './SubscriptionTiers';
import { createCheckoutSession } from '../lib/stripe';
import { AlertCircle } from 'lucide-react';
import { SignaturePad } from './SignaturePad';

interface CardEditorProps {
  cardImage: string;
  message: string;
  recipientName: string;
  occasion: string;
  signatureUrl?: string | null;
  onApprove: (data: { front: string; back: string; to: any }) => void;
  onRequestChanges: () => void;
  onClose: () => void;
  recipientAddress?: {
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
  userId: string;
}

// Add constants to match Lob template
const ASPECT_RATIO = 6.25 / 4.25;  // width/height ratio for 6.25" x 4.25" template

// Add constants for text customization
const FONT_OPTIONS = {
  default: 'Arial',
  serif: 'Times New Roman',
  mono: 'Courier New',
  cursive: 'Brush Script MT'
};

const FONT_SIZES = {
  small: '12px',
  medium: '14px',
  large: '16px'
};

type Step = 'customize' | 'preview' | 'review';

export function CardEditor({
  cardImage,
  message: initialMessage,
  recipientName,
  occasion: initialOccasion,
  signatureUrl: initialSignatureUrl,
  onApprove,
  onRequestChanges,
  onClose,
  recipientAddress,
  userId
}: CardEditorProps) {
  const [currentStep, setCurrentStep] = useState<Step>('customize');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(initialSignatureUrl || null);
  
  // Text customization state
  const [message, setMessage] = useState(initialMessage);
  const [textAlignment, setTextAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [fontFamily, setFontFamily] = useState<keyof typeof FONT_OPTIONS>('default');
  const [redirectTimer, setRedirectTimer] = useState<NodeJS.Timeout | null>(null);
  const [freeCardsAvailable, setFreeCardsAvailable] = useState(0);
  const [showSubscriptionTiers, setShowSubscriptionTiers] = useState(false);
  const [cardsUsedThisMonth, setCardsUsedThisMonth] = useState(0);
  const [subscription, setSubscription] = useState<any>(null);

  const hasAvailableCredits = freeCardsAvailable > cardsUsedThisMonth;
  const hasActiveSubscription = subscription?.status === 'active';
  const isCardFree = hasAvailableCredits || hasActiveSubscription;

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [userId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [redirectTimer]);

  async function fetchSubscriptionStatus() {
    try {
      // Get user's subscription status
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .rpc('get_user_subscription', { user_id: userId });

      if (subscriptionError) {
        console.error('Error fetching subscription:', subscriptionError);
      }

      setSubscription(subscriptionData);

      // Get user's credits using RPC function
      const { data: creditsData, error: creditsError } = await supabase
        .rpc('get_user_credits_with_expiration');

      if (creditsError) {
        console.error('Error fetching credits:', creditsError);
      }

      // Set the credits from the response
      if (creditsData && creditsData.length > 0) {
        setFreeCardsAvailable(creditsData[0].total_credits);
        // For now, we'll use total_credits - credits_remaining as used credits
        const usedCredits = creditsData[0].purchases.reduce((total: number, purchase: any) => {
          return total + (purchase.credits_amount - purchase.credits_remaining);
        }, 0);
        setCardsUsedThisMonth(usedCredits);
      }

      // Log the data for debugging
      console.log('Subscription:', subscriptionData);
      console.log('Credits:', creditsData);

    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  }

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Check if user has available cards
      if (!isCardFree) {
        setShowSubscriptionTiers(true);
        setIsSubmitting(false);
        return;
      }

      // Validate recipient address
      if (!recipientAddress?.address_line1 || !recipientAddress?.address_city || 
          !recipientAddress?.address_state || !recipientAddress?.address_zip) {
        setError('Recipient address is incomplete. Please update the contact with a complete mailing address.');
        setIsSubmitting(false);
        return;
      }

      // Create HTML templates for front and back
      const frontHtml = createFrontTemplate(cardImage);
      const backHtml = createBackTemplate(
        `Dear ${recipientName},\n\n${message}\n\nBest Wishes,`,
        initialOccasion,
        signatureUrl || undefined,
        {
          font: FONT_OPTIONS[fontFamily],
          alignment: textAlignment,
          fontSize: FONT_SIZES[fontSize]
        }
      );

      // Create the order
      const { data: orderData, error: orderError } = await supabase.functions.invoke('create-lob-order', {
        body: {
          description: `Card for ${recipientName}`,
          to: {
            name: recipientName,
            address_line1: recipientAddress.address_line1,
            address_city: recipientAddress.address_city,
            address_state: recipientAddress.address_state,
            address_zip: recipientAddress.address_zip
          },
          front: frontHtml,
          back: backHtml
        }
      });

      if (orderError) throw orderError;
      if (!orderData?.id) throw new Error('No order ID returned from Lob');

      // Show success state and set up redirect
      setOrderData(orderData);
      setShowSuccess(true);
      
      const timer = setTimeout(() => {
        handleCloseAndRedirect();
      }, 10000);
      setRedirectTimer(timer);

    } catch (error) {
      console.error('Error creating card order:', error);
      setError(error instanceof Error ? error.message : 'Failed to create card order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAndRedirect = () => {
    // Clear any existing timer
    if (redirectTimer) {
      clearTimeout(redirectTimer);
      setRedirectTimer(null);
    }

    // Clean up local state
    setShowSuccess(false);
    setOrderData(null);
    
    // Close all modals and return to dashboard
    // We don't need to call onApprove again since the order is already created
    onClose();
    onRequestChanges();
  };

  const handleSubscribe = async (tier: string) => {
    try {
      const priceId = tier === 'basic' ? 'price_RjZ8s24Zn23gsp' :
                      tier === 'standard' ? 'price_RjVfomf8LFz4yA' :
                      'price_RjZCheZhAiiVpP';
      
      await createCheckoutSession('subscription', priceId);
      setShowSubscriptionTiers(false);
    } catch (error) {
      console.error('Error subscribing:', error);
      // Handle error (show error message to user)
    }
  };

  const handleSignatureSave = async (dataUrl: string) => {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Upload signature
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const fileExt = 'png';
      const fileName = `${session.user.id}-${Math.random()}.${fileExt}`;
      const filePath = `signatures/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, blob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('users')
        .update({ signature_url: publicUrl })
        .eq('user_id', session.user.id);

      if (updateError) throw updateError;

      setSignatureUrl(publicUrl);
      setShowSignaturePad(false);
    } catch (error) {
      console.error('Error saving signature:', error);
      setError('Failed to save signature. Please try again.');
    }
  };

  // Helper function to render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'customize':
        return (
          <div className="flex flex-col h-full">
            {/* Preview Section - Fixed below header on both mobile and desktop */}
            <div className="flex-shrink-0 bg-gray-50 p-4">
              <div className="w-full max-w-[600px] mx-auto">
                <CardTemplate
                  message={message}
                  recipientName={recipientName}
                  recipientAddress={recipientAddress}
                  signatureUrl={signatureUrl}
                  fontFamily={FONT_OPTIONS[fontFamily]}
                  fontSize={FONT_SIZES[fontSize]}
                  textAlignment={textAlignment}
                  isPreview={true}
                />
              </div>
            </div>

            {/* Customization Options - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Your Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full p-3 border rounded-lg min-h-[150px] text-base focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    placeholder="Write your message here..."
                  />
                </div>

                {/* Signature */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Your Signature</label>
                  <div className="space-y-4">
                    {signatureUrl ? (
                      <div className="relative">
                        <img
                          src={signatureUrl}
                          alt="Your signature"
                          className="max-w-full h-auto max-h-20"
                        />
                        <button
                          onClick={() => setShowSignaturePad(true)}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                        >
                          <PenSquare className="w-4 h-4 text-brand-secondary" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSignaturePad(true)}
                        className="w-full p-4 border-2 border-dashed rounded-lg text-brand-secondary hover:text-brand-primary hover:border-brand-primary transition-colors"
                      >
                        <PenSquare className="w-6 h-6 mx-auto mb-2" />
                        <span>Draw Your Signature</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Font Style */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Font Style</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(FONT_OPTIONS).map(([key, font]) => (
                      <button
                        key={key}
                        onClick={() => setFontFamily(key as keyof typeof FONT_OPTIONS)}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          fontFamily === key 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                        style={{ fontFamily: font }}
                      >
                        Aa
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Font Size</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(FONT_SIZES).map(([key, size]) => (
                      <button
                        key={key}
                        onClick={() => setFontSize(key as keyof typeof FONT_SIZES)}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          fontSize === key 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                        style={{ fontSize: size }}
                      >
                        Aa
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Alignment */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Text Alignment</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['left', 'center', 'right'].map((align) => (
                      <button
                        key={align}
                        onClick={() => setTextAlignment(align as 'left' | 'center' | 'right')}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          textAlignment === align 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                      >
                        {align === 'left' && 'Left'}
                        {align === 'center' && 'Center'}
                        {align === 'right' && 'Right'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'preview':
        return (
          <div className="h-full flex flex-col">
            {/* Scrollable Container */}
            <div className="flex-1 overflow-y-auto">
              {/* Front Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Front</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <div className="relative" style={{ paddingTop: `${(4.25 / 6.25) * 100}%` }}>
                      <img
                        src={cardImage}
                        alt="Card front"
                        className="absolute inset-0 w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Back Preview */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Back</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <CardTemplate
                      message={message}
                      recipientName={recipientName}
                      recipientAddress={recipientAddress}
                      signatureUrl={signatureUrl}
                      fontFamily={FONT_OPTIONS[fontFamily]}
                      fontSize={FONT_SIZES[fontSize]}
                      textAlignment={textAlignment}
                      isPreview={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="h-full overflow-y-auto">
            {/* Cost Breakdown */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-brand-secondary mb-4">Cost Breakdown</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-brand-secondary opacity-75" />
                    <span>One-time Purchase</span>
                  </div>
                  <span className={isCardFree ? 'line-through text-gray-400' : ''}>
                    $3.50
                  </span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-brand-primary" />
                      <span>Total</span>
                    </div>
                    <span className="text-brand-primary">
                      {isCardFree ? 'FREE' : '$3.50'}
                    </span>
                  </div>
                  {hasAvailableCredits && (
                    <p className="text-sm text-green-600 mt-2">
                      Using 1 credit from your available credits ({freeCardsAvailable - cardsUsedThisMonth} remaining)
                    </p>
                  )}
                  {hasActiveSubscription && !hasAvailableCredits && (
                    <p className="text-sm text-green-600 mt-2">
                      Free with your {subscription.price_id === 'price_1Qq2ecPcJBAkjOG46gNS7Qo5' ? 'Standard' : 'Premium'} subscription
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Final Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="w-full max-w-[600px] mx-auto">
                <CardTemplate
                  message={message}
                  recipientName={recipientName}
                  recipientAddress={recipientAddress}
                  signatureUrl={signatureUrl}
                  fontFamily={FONT_OPTIONS[fontFamily]}
                  fontSize={FONT_SIZES[fontSize]}
                  textAlignment={textAlignment}
                  isPreview={true}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  // Helper function to get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 'customize':
        return 'Customize Your Card';
      case 'preview':
        return 'Preview Your Card';
      case 'review':
        return 'Review & Send';
    }
  };

  // Helper function to get action button text
  const getActionButtonText = () => {
    if (isSubmitting) return 'Sending...';
    switch (currentStep) {
      case 'customize':
        return 'Continue to Preview';
      case 'preview':
        return 'Continue to Review';
      case 'review':
        return isCardFree ? 'Send Card' : 'Purchase & Send';
    }
  };

  // Helper function to handle next step
  const handleNextStep = () => {
    if (currentStep === 'review') {
      handleApprove();
    } else {
      setCurrentStep(currentStep === 'customize' ? 'preview' : 'review');
    }
  };

  if (showSuccess && orderData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-md flex flex-col min-h-[300px]">
          {/* Success Content */}
          <div className="flex-1 p-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold font-display text-brand-secondary mb-2">
                Card Ordered Successfully!
              </h3>
              <p className="text-brand-secondary mb-6 text-center">
                Your {initialOccasion.toLowerCase()} card for {recipientName} has been ordered and will be delivered soon.
              </p>
              <div className="text-sm text-brand-secondary opacity-75">
                Returning to dashboard in 10 seconds...
              </div>
            </div>
          </div>

          {/* Action Button - Fixed at bottom */}
          <div className="flex-shrink-0 border-t bg-white p-4">
            <button
              onClick={handleCloseAndRedirect}
              className="w-full px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showSubscriptionTiers) {
    return (
      <SubscriptionTiers
        onClose={() => setShowSubscriptionTiers(false)}
        onSelectTier={handleSubscribe}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl flex flex-col h-[90vh]">
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary">
            {getStepTitle()}
          </h2>
          <button 
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message - if present */}
        {error && (
          <div className="flex-shrink-0 p-4 bg-red-50 border-b border-red-200 flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {currentStep === 'customize' && (
            <div className="flex flex-col h-full">
              {/* Preview Section - Fixed below header on both mobile and desktop */}
              <div className="flex-shrink-0 bg-gray-50 p-4">
                <div className="w-full max-w-[600px] mx-auto">
                  <CardTemplate
                    message={message}
                    recipientName={recipientName}
                    recipientAddress={recipientAddress}
                    signatureUrl={signatureUrl}
                    fontFamily={FONT_OPTIONS[fontFamily]}
                    fontSize={FONT_SIZES[fontSize]}
                    textAlignment={textAlignment}
                    isPreview={true}
                  />
                </div>
              </div>

              {/* Customization Options - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-3xl mx-auto space-y-6">
                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Your Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full p-3 border rounded-lg min-h-[150px] text-base focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      placeholder="Write your message here..."
                    />
                  </div>

                  {/* Signature */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Your Signature</label>
                    <div className="space-y-4">
                      {signatureUrl ? (
                        <div className="relative">
                          <img
                            src={signatureUrl}
                            alt="Your signature"
                            className="max-w-full h-auto max-h-20"
                          />
                          <button
                            onClick={() => setShowSignaturePad(true)}
                            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                          >
                            <PenSquare className="w-4 h-4 text-brand-secondary" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowSignaturePad(true)}
                          className="w-full p-4 border-2 border-dashed rounded-lg text-brand-secondary hover:text-brand-primary hover:border-brand-primary transition-colors"
                        >
                          <PenSquare className="w-6 h-6 mx-auto mb-2" />
                          <span>Draw Your Signature</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Font Style */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Font Style</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(FONT_OPTIONS).map(([key, font]) => (
                        <button
                          key={key}
                          onClick={() => setFontFamily(key as keyof typeof FONT_OPTIONS)}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            fontFamily === key 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                          style={{ fontFamily: font }}
                        >
                          Aa
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Size */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Font Size</label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(FONT_SIZES).map(([key, size]) => (
                        <button
                          key={key}
                          onClick={() => setFontSize(key as keyof typeof FONT_SIZES)}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            fontSize === key 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                          style={{ fontSize: size }}
                        >
                          Aa
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text Alignment */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Text Alignment</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['left', 'center', 'right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => setTextAlignment(align as 'left' | 'center' | 'right')}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            textAlignment === align 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                        >
                          {align === 'left' && 'Left'}
                          {align === 'center' && 'Center'}
                          {align === 'right' && 'Right'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'preview' && (
            <div className="p-4 space-y-6">
              {/* Front Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Front</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <div className="relative" style={{ paddingTop: `${(4.25 / 6.25) * 100}%` }}>
                      <img
                        src={cardImage}
                        alt="Card front"
                        className="absolute inset-0 w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Back Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Back</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <CardTemplate
                      message={message}
                      recipientName={recipientName}
                      recipientAddress={recipientAddress}
                      signatureUrl={signatureUrl}
                      fontFamily={FONT_OPTIONS[fontFamily]}
                      fontSize={FONT_SIZES[fontSize]}
                      textAlignment={textAlignment}
                      isPreview={true}
                    />
                  </div>
                </div>
              </div>

              {/* Cost Section */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Cost</h3>
                <div className="bg-white rounded-lg border p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span>Card Price</span>
                      </div>
                      <span className={isCardFree ? 'line-through text-gray-400' : ''}>
                        $3.50
                      </span>
                    </div>
                    {hasAvailableCredits && (
                      <span className="text-sm text-green-600 mt-2 ">
                        Free using 1 credit. ({freeCardsAvailable} remaining)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer with Navigation */}
        <div className="flex-shrink-0 border-t bg-white p-4">
          <div className="flex justify-between items-center max-w-4xl mx-auto w-full">
            <button
              onClick={() => currentStep === 'customize' ? onClose() : setCurrentStep(currentStep === 'preview' ? 'customize' : 'preview')}
              className="flex items-center gap-2 px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {currentStep === 'customize' ? 'Cancel' : 'Back'}
            </button>
            <button
              onClick={handleNextStep}
              disabled={isSubmitting}
              className={`px-6 py-2 rounded-lg transition-all ${
                isSubmitting
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-primary text-white hover:bg-brand-secondary'
              }`}
            >
              {getActionButtonText()}
              {currentStep === 'preview' && !isCardFree && <span> ($3.50)</span>}
            </button>
          </div>
        </div>
      </div>

      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onClose={() => setShowSignaturePad(false)}
        />
      )}
    </div>
  );
}